import React, { useEffect, useMemo, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Dialog } from "primereact/dialog";
import { apiService, ServiceType } from "../../services/apiService";
import { useToast } from "../../contexts/ToastContext";
import { useSearch } from "../../contexts/SearchContext";
import { InputIcon } from "primereact/inputicon";
import { IconField } from "primereact/iconfield";

const ServiceTypesAdmin: React.FC = () => {
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const [loading, setLoading] = useState(false);
  const { searchTerm: filter, setSearchTerm: setFilter } = useSearch();
  const [modalVisible, setModalVisible] = useState(false);
  const [editServiceType, setEditServiceType] = useState<ServiceType | null>(
    null
  );
  const [formName, setFormName] = useState("");

  const toast = useToast();

  const load = async () => {
    setLoading(true);
    const res = await apiService.getServiceTypes();
    if (res.success) setServiceTypes(res.data);
    else
      toast.showError("Failed", res.message || "Unable to load service types");
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(
    () =>
      serviceTypes.filter((st) =>
        st.name.toLowerCase().includes(filter.toLowerCase())
      ),
    [serviceTypes, filter]
  );

  const openNew = () => {
    setEditServiceType(null);
    setFormName("");
    setModalVisible(true);
  };

  const openEdit = (serviceType: ServiceType) => {
    setEditServiceType(serviceType);
    setFormName(serviceType.name);
    setModalVisible(true);
  };

  const save = async () => {
    if (!formName.trim()) {
      toast.showWarn("Validation", "Name is required");
      return;
    }

    const payload = {
      id: editServiceType?.id,
      name: formName.trim(),
    };

    const res = await apiService.createOrUpdateServiceType(payload);
    if (res.success) {
      toast.showSuccess("Saved", `Service type '${res.data.name}' saved`);
      setModalVisible(false);
      await load();
    } else {
      toast.showError("Error", res.message || "Save failed");
    }
  };

  const remove = async (serviceType: ServiceType) => {
    const res = await apiService.deleteServiceType(serviceType.id);
    if (res.success) {
      toast.showSuccess(
        "Deleted",
        `Service type '${serviceType.name}' deleted`
      );
      await load();
    } else {
      toast.showError("Error", res.message || "Delete failed");
    }
  };

  const header = (
    <div className="flex justify-content-between align-items-center">
      <div className="flex align-items-center gap-2">
        <IconField iconPosition="left">
          <InputIcon className="pi pi-search" />
          <InputText
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search service types..."
          />
        </IconField>
      </div>
      <Button label="Add" icon="pi pi-plus" onClick={openNew} />
    </div>
  );

  return (
    <div>
      <DataTable
        value={filtered}
        loading={loading}
        header={header}
        emptyMessage="No service types found"
        className="p-datatable-sm"
        paginator
        rows={10}
      >
        <Column field="name" header="Name" sortable />
        <Column
          header="Actions"
          body={(row: ServiceType) => (
            <div className="flex gap-2 justify-content-end">
              <Button
                icon="pi pi-pencil"
                className="p-button-rounded p-button-outlined p-button-sm"
                onClick={() => openEdit(row)}
              />
              <Button
                icon="pi pi-trash"
                className="p-button-rounded p-button-outlined p-button-sm p-button-danger"
                onClick={() => remove(row)}
              />
            </div>
          )}
          style={{ width: "120px" }}
          headerStyle={{ textAlign: "right" }}
        />
      </DataTable>

      <Dialog
        header={editServiceType ? "Edit Service Type" : "Add Service Type"}
        visible={modalVisible}
        onHide={() => setModalVisible(false)}
        style={{ width: 420 }}
        modal
      >
        <div className="flex flex-column gap-3">
          <div className="field">
            <label className="block mb-2">Name</label>
            <InputText
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              className="w-full"
            />
          </div>
          <div className="flex justify-content-end gap-2 mt-3">
            <Button
              label="Cancel"
              className="p-button-text"
              onClick={() => setModalVisible(false)}
            />
            <Button label="Save" icon="pi pi-check" onClick={save} />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default ServiceTypesAdmin;
